import { Collection, LongString, MaxString, ModelBase, NormalString, ObservableCollection, Property, Required, VeryLongString } from '@skylab-shared/core';
import { AddressModel } from '../address/address.model';
import { CalculationMethod } from '../common/calculation-method';
import { VatReleaseReasonSource } from '../company/vat-release-reason-source';
import { DocumentBuyerModel } from '../document/document-buyer.model';
import { DocumentClientModel } from '../document/document-client.model';
import { ExchangeRateModel } from '../exchange-rate/exchange-rate.model';
import { VatTableItemModel } from '../vat-table/vat-table-item.model';
import { SaleDocumentItemModel } from './sale-document-item.model';
import { SaleDocumentKind } from './sale-document-kind';
import { SalePatternIssueBusinessDayMode } from './sale-pattern-issue-business-day-mode';
import { SalePatternKsefSendOption } from './sale-pattern-ksef-send-option';
import { SalePatternSchedule, SalePatternScheduleRepetitionSkip } from './sale-pattern-schedule';
import { RateNP } from './rate-np';
import { SplitPaymentState } from './split-payment-state';

export class SalePatternModel extends ModelBase {
    @Property({ identifiable: true })
    public id: number;

    @Property()
    public active: boolean;

    @Property(Number, { nullable: true })
    public bankAccountNumberId?: number | null;

    @Property(Number,{ nullable: true })
    public baseCurrencyBankAccountNumberId?: number | null;

    @Property(DocumentBuyerModel, { nullable: true })
    public buyer: DocumentBuyerModel | null;

    @Property()
    @Required()
    public calculationMethod: CalculationMethod;

    @Property()
    @Required('data-model.document.validation.pattern-name.non-empty')
    @VeryLongString('data-model.document.validation.pattern-name.length')
    public name: string;

    @Property(String, { nullable: true })
    public schedule: SalePatternSchedule | null;

    @Property(Number,{ nullable: true })
    public issueDay: number | null;

    @Property(Number,{ nullable: true })
    public issueMonth: number | null;

    @Property(Number, { nullable: true })
    public repetitionSkip?: SalePatternScheduleRepetitionSkip | null;

    @Property(DocumentClientModel, {nullable: true})
    public client: DocumentClientModel | null;

    @Property()
    public clientEmails: string[];

    @Property()
    @VeryLongString('data-model.document.validation.client-email.length')
    public clientEmailsPlainText: string;

    @Property()
    @Required()
    public communityTransactionId: number;

    @Property(String, { nullable: true })
    public rateNP: RateNP | null;

    @Property(Boolean, { nullable: true })
    public reverseCharge: boolean | null;

    @Property()
    @Required()
    public currencyId: number;

    @Property()
    public deliveryAddress: AddressModel;

    @Property()
    public dueDateInDays: number;

    @Property(Date,{ nullable: true })
    public issuingEndDate: Date | null;

    @Property(Date,{ nullable: true })
    public issuingStartDate: Date | null;

    @Property()
    public exchangeRate: ExchangeRateModel;

    @Property()
    public grossValue: number;

    @Property()
    public issuedByVatPayer: boolean;

    @Property({ nullable: true })
    public vatReleaseReasonId?: number;

    @Property({ nullable: true })
    public vatReleaseReasonCustomName?: string;

    @Property({ nullable: true })
    public vatReleaseReasonSource?: VatReleaseReasonSource;

    @Property()
    public issuer: string;

    @Property()
    public issuerUserId: number;

    @Collection({ type: SaleDocumentItemModel })
    public items: ObservableCollection<SaleDocumentItemModel>;

    @Property(Boolean, { nullable: true })
    public issueInBusinessDay: boolean | null;

    @Property({ nullable: true })
    public issueInBusinessDayMode: SalePatternIssueBusinessDayMode;

    @Property()
    public jpkVatGroups: string[];

    @Property()
    public invoiceToReceipt: boolean;

    @Property()
    public kind: SaleDocumentKind;

    @Property({ nullable: true })
    public mailTemplateId?: number;

    @Property()
    @MaxString('data-model.document.validation.note.length')
    public note: string;

    @Property({ nullable: true })
    public numberingId: number;

    @Property()
    public sendEmailToIssuer: boolean;

    @Property()
    @Required()
    public paymentMethodId: number;

    @Property()
    @NormalString('data-model.document.validation.recipient.length')
    public recipient: string;

    @Property()
    public sendDocumentToClient: boolean;

    @Property()
    public sendDocumentCopyToIssuer: boolean;

    @Property()
    public splitPayment: SplitPaymentState;

    @Property()
    public autoPayment: boolean;

    @Property()
    @Required('data-model.document.validation.transaction-location.non-empty')
    @LongString('data-model.document.validation.transaction-location.length')
    public transactionLocation: string;

    @Collection({ type: VatTableItemModel })
    public vatTableItems: ObservableCollection<VatTableItemModel>;

    @Collection({ type: VatTableItemModel })
    public vatTableItemsInBaseCurrency: ObservableCollection<VatTableItemModel>;

    @Property({ nullable: true })
    public wstoShipmentCountryId: number;

    @Property(Number, { nullable: true })
    public wstoConsumerCountryId: number | null;

    @Property({ nullable: true })
    @MaxString('data-model.document.validation.wsto-additional-information.length')
    public wstoAdditionalInformation: string;

    @Property()
    @Required()
    public ksefSendOption: SalePatternKsefSendOption;
}
